import React, { useState, useEffect } from "react";
import { Table, Input, Space, Popconfirm, Tooltip } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PoweroffOutlined,
  StarFilled,
  StarOutlined,
  KeyOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";

const GenericTable = ({
  data,
  columnsConfig,
  searchKeys = [],
  actions = [],
  actionFunctions = [],
  rowKey = "id",
  searchDisable = false,
  pagination = {}, // Accept pagination props from the parent
}) => {
  const [filteredData, setFilteredData] = useState(data);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  const getNestedValue = (obj, path) => {
    return path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : null), obj);
  };

  const effectiveSearchKeys =
    searchKeys && searchKeys.length > 0
      ? searchKeys
      : columnsConfig
          .filter((col) => col.dataIndex)
          .map((col) => col.dataIndex);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter((item) =>
      effectiveSearchKeys.some((key) => {
        const path = Array.isArray(key) ? key : key.toString().split(".");
        const cellValue = getNestedValue(item, path)?.toString().toLowerCase();
        return cellValue?.includes(value);
      })
    );

    setFilteredData(filtered);
  };

  const generateColumns = () => {
    const generatedColumns = columnsConfig?.map((col) => ({
      title: col.title || (Array.isArray(col.dataIndex) ? col.dataIndex.join(".") : col.dataIndex),
      dataIndex: col.dataIndex,
      key: Array.isArray(col.dataIndex) ? col.dataIndex.join(".") : col.dataIndex,
      sorter: col.sortable
        ? (a, b) => {
            const path = Array.isArray(col.dataIndex) ? col.dataIndex : col.dataIndex.toString().split(".");
            const aValue = getNestedValue(a, path);
            const bValue = getNestedValue(b, path);
            if (typeof aValue === "string") {
              return aValue.localeCompare(bValue);
            }
            return aValue - bValue;
          }
        : null,
      render: col.render || ((text, record) => {
        const path = Array.isArray(col.dataIndex) ? col.dataIndex : col.dataIndex.toString().split(".");
        return getNestedValue(record, path) || text;
      }),
      width: col.width || 150,
    }));

    if (actions.length > 0 && actionFunctions.length === actions.length) {
      generatedColumns.push({
        key: "actions",
        fixed: "right",
        render: (text, record) => (
          <Space size="middle">
            {actions.map((action, index) => {
              const handleClick = () => actionFunctions[index](record);
              switch (action.toLowerCase()) {
                case "delete":
                  return (
                    <Tooltip title="Delete" key="delete">
                      <Popconfirm
                        title="Are you sure to delete this item?"
                        onConfirm={handleClick}
                        okText="Yes"
                        cancelText="No"
                      >
                        <DeleteOutlined
                          style={{ color: "red", fontSize: "18px", cursor: "pointer" }}
                        />
                      </Popconfirm>
                    </Tooltip>
                  );
                case "edit":
                  return (
                    <Tooltip title="Edit" key="edit">
                      <EditOutlined
                        style={{ fontSize: "18px", cursor: "pointer" }}
                        onClick={handleClick}
                      />
                    </Tooltip>
                  );
                case "view":
                  return (
                    <Tooltip title="View" key="view">
                      <EyeOutlined
                        style={{ fontSize: "18px", cursor: "pointer" }}
                        onClick={handleClick}
                      />
                    </Tooltip>
                  );
                default:
                  return null;
              }
            })}
          </Space>
        ),
        width: actions.length * 60,
      });
    }

    return generatedColumns;
  };

  return (
    <div className="generic-table-container">
      {!searchDisable && (
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={handleSearch}
          style={{ marginBottom: 16, width: 300 }}
        />
      )}
      <Table
        dataSource={filteredData}
        columns={generateColumns()}
        rowKey={(record) => record[rowKey]}
        pagination={{
          current: pagination.current || 1,
          pageSize: pagination.pageSize || 10,
          total: pagination.total || 0,
          onChange: pagination.onChange,
        }}
        scroll={{ x: true }}
      />
    </div>
  );
};

export default GenericTable;
